import { Switch } from '@thanx/uikit/switch'
import { withFormsy } from 'formsy-react'
import { PassDownProps } from 'formsy-react/dist/withFormsy'
import { useCallback, useMemo } from 'react'
import { Button } from 'react-bootstrap'

import EnhancedMultiSelect from 'components/Form/EnhancedMultiSelect'
import Spinner from 'components/Spinner'
import { useModifierSelectData } from 'hooks/useModifierSelectData'
import { buildTranslate } from 'locales'
import ModifierIdInput from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierIdInput'
import ModifierItems from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierItems'
import ModifierPopover, {
  ModifierPopoverVariant,
} from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierPopover'
import { useFormModel } from 'scenes/RedeemManager/Builder/FormModel'
import { buildModifierOptionName } from 'scenes/RedeemManager/helpers'

import type { Option } from 'baseui/select'
import type { Fields as Modifier } from 'models/Modifier'

type ModifierSelectProps = PassDownProps<string[]> & {
  label: string
  helpText?: string
  secondaryHelpText?: string
  fallbackHelpText?: string
  className?: string
  productIds?: string[]
  toggleVisibility?: (visible: boolean) => void
  hideLabel?: string
  // Replaces the default hide (clear and collapse) with a custom action
  onHide?: () => void
  shouldDisplayPopover?: boolean
  popoverVariant?: ModifierPopoverVariant
}

const t = buildTranslate('redeem_manager.modifier_select')

const ModifierSelect = (props: ModifierSelectProps) => {
  const {
    name,
    value: selectedIds = [],
    productIds = [],
    setValue,
    label,
    secondaryHelpText,
    fallbackHelpText,
    toggleVisibility,
    hideLabel,
    onHide,
    helpText,
    className = '',
    shouldDisplayPopover = false,
    popoverVariant,
  } = props

  const { setModel, model } = useFormModel()

  const {
    modifiersData: { modifiers, pagination, isLoading },
    searchState: {
      searchTerm,
      setSearchTerm,
      setPage,
      isFocused,
      setIsFocused,
    },
    selection: {
      selectedModifiers,
      setSelectedModifiers,
      isModifierSelectedBy,
      isResolvingSelection,
    },
    uiState: { shouldDisplayIdInput },
  } = useModifierSelectData(selectedIds, productIds, setValue)

  const getCustomLabel = useCallback((option?: Option) => {
    const name = option?.name || option?.label || ''
    const [boldText, regularText] = name.split(' - ', 2)

    return (
      <>
        <strong>{boldText}</strong>
        {regularText && ` - ${regularText}`}
      </>
    )
  }, [])

  const resetValues = () => {
    setModel({ ...model, [name]: [] })
    setSearchTerm('')
    setPage(1)
  }

  const options: Option[] = useMemo(() => {
    return modifiers
      .filter(
        modifier =>
          modifier.option &&
          modifier.items?.length &&
          modifier.items.some(item => productIds.includes(item.chain_uid)) &&
          modifier.option.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map(modifier => ({
        ...modifier.option,
        name: buildModifierOptionName(modifier),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [modifiers, productIds, searchTerm])

  const selectedModifierOptions = selectedModifiers.map(mod => mod.option)

  const deleteItem = (uid: string) => {
    const newSelectedIds = selectedIds.filter(id => id !== uid)
    setValue(newSelectedIds)
    setSelectedModifiers(prev =>
      prev.filter(modifier => !isModifierSelectedBy([uid])(modifier))
    )
  }

  const handleChange = (selections: Option[]) => {
    const newSelectedIds = selections.map(option => option.chain_uid)
    setValue(newSelectedIds)

    setSelectedModifiers(prev => {
      const prevMap = new Map(
        prev.map(modifier => [modifier.option.chain_uid, modifier])
      )
      return newSelectedIds
        .map(
          id => modifiers.find(isModifierSelectedBy([id])) ?? prevMap.get(id)
        )
        .filter(Boolean) as Modifier[]
    })
  }

  const handleFocusAndBlur = (focusState: boolean) => () =>
    setIsFocused(focusState)

  const toggleModifierVisibility = () => {
    if (onHide) {
      onHide()
      return
    }
    toggleVisibility?.(false)
    resetValues()
  }
  const canHide = Boolean(toggleVisibility || onHide)

  const getPlaceholder = () => (isFocused ? t('search') : t('select_modifier'))

  if (shouldDisplayIdInput) {
    return (
      <ModifierIdInput
        label={label}
        name={name}
        helpText={fallbackHelpText || helpText}
        secondaryHelpText={secondaryHelpText}
        className={className}
        selectedIds={selectedIds}
        setValue={setValue}
        toggleVisibility={canHide ? toggleModifierVisibility : undefined}
        hideLabel={hideLabel}
        shouldDisplayPopover={shouldDisplayPopover}
        popoverVariant={popoverVariant}
      />
    )
  }

  return (
    <div className={`${className} mt-l`}>
      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-row">
          <p className="body-text-14 bold mb-0">{label}</p>
          <Switch condition={shouldDisplayPopover}>
            <ModifierPopover variant={popoverVariant} />
          </Switch>
        </div>
        <Switch condition={canHide}>
          <Button
            bsStyle="link"
            type="button"
            onClick={toggleModifierVisibility}
            className="p-0 m-0"
          >
            {hideLabel ?? t('wrapper.hide')}
          </Button>
        </Switch>
      </div>

      <Switch condition={Boolean(helpText)}>
        <p className="mt-xs body-text-4 grey-70">{helpText}</p>
      </Switch>

      <div className="d-flex flex-row mt-xs">
        <EnhancedMultiSelect
          pagination={pagination}
          options={options}
          onSearch={setSearchTerm}
          onPageChange={setPage}
          isLoading={isLoading}
          placeholder={getPlaceholder()}
          maxDropdownHeight="400px"
          labelKey="name"
          valueKey="chain_uid"
          valueName="modifier"
          isFocused={isFocused}
          disabled={!productIds.length || isResolvingSelection}
          value={isResolvingSelection ? [] : selectedModifierOptions}
          onFocus={handleFocusAndBlur(true)}
          onBlur={handleFocusAndBlur(false)}
          onChange={handleChange}
          getCustomLabel={getCustomLabel}
        />
      </div>

      <div className="mt-s">
        {/* While saved ids resolve, the list would render page by page and
            editing would drop the unresolved ids — hold a loading state. */}
        <Switch
          condition={!isResolvingSelection}
          fallback={
            <div className="d-flex flex-row align-items-center" role="status">
              <span aria-hidden="true">
                <Spinner isLoading size="1x" />
              </span>
              <p className="body-text-4 grey-70 mb-0 ml-xs">
                {t('resolving_selection', { count: selectedIds.length })}
              </p>
            </div>
          }
        >
          <ModifierItems
            deleteItem={deleteItem}
            orderedSelectedModifiers={selectedModifiers}
            productIds={productIds}
          />
        </Switch>
      </div>
    </div>
  )
}

export default withFormsy(ModifierSelect)
